import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, Languages } from "lucide-react";
import { useI18n } from "../i18n";

export default function Header() {
  const { t, lang, buildPath, switchLang } = useI18n();
  const [open, setOpen] = useState(false);
  const { hash, pathname } = useLocation();
  const home = buildPath("home");

  // Nav items with in-page anchors (single set, no duplicates)
  const navItems = [
    { key: "home", href: home, testid: "nav-home" },
    { key: "benefits", href: `${home}#benefits`, testid: "nav-benefits" },
    { key: "education", href: `${home}#education`, testid: "nav-education" },
    { key: "faq", href: `${home}#faq`, testid: "nav-faq" },
  ];

  const closeMenu = () => setOpen(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-[#0B2545] border-b-4 border-[#006837]" data-testid="site-header">
      {/* Tricolor accent bar */}
      <div className="h-1 w-full flex" aria-hidden="true">
        <div className="flex-1 bg-[#006837]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#CE1126]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between gap-4">
          <Link to={home} className="flex items-center gap-3 group" data-testid="header-logo" onClick={closeMenu}>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white text-[#0B2545] font-heading font-black text-lg">
              S
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-white font-heading font-extrabold text-lg tracking-tight">
                {t("header.brand")}
              </span>
              <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[#C59B27]">
                <ShieldCheck className="w-3 h-3" aria-hidden="true" />
                {t("header.badge")}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                data-testid={item.testid}
                className="px-3 py-2 text-sm font-medium text-slate-100 hover:text-white hover:bg-white/10 rounded-md"
              >
                {t(`header.nav.${item.key}`)}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={switchLang}
              data-testid={lang === "mx" ? "lang-switch-en" : "lang-switch-mx"}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white border border-white/30 rounded-md hover:bg-white/10"
              aria-label={t("header.switchLang")}
            >
              <Languages className="w-4 h-4" aria-hidden="true" />
              {t("header.switchLang")}
            </button>
            <a
              href={`${home}#newsletter`}
              data-testid="header-cta"
              className="inline-flex items-center px-4 py-2.5 text-sm font-bold rounded-md bg-[#006837] text-white hover:bg-[#004B28]"
            >
              {t("header.cta")}
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md text-white border border-white/30 hover:bg-white/10"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden pb-5 border-t border-white/10 mt-0" data-testid="mobile-nav">
            <nav className="flex flex-col gap-1 pt-4">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={closeMenu}
                  data-testid={`mobile-${item.testid}`}
                  className="px-3 py-3 text-base font-medium text-white rounded-md hover:bg-white/10"
                >
                  {t(`header.nav.${item.key}`)}
                </a>
              ))}
              <div className="flex flex-col gap-2 pt-3 mt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => { switchLang(); closeMenu(); }}
                  data-testid={lang === "mx" ? "mobile-lang-switch-en" : "mobile-lang-switch-mx"}
                  className="inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-semibold text-white border border-white/30 rounded-md hover:bg-white/10"
                >
                  <Languages className="w-4 h-4" aria-hidden="true" />
                  {t("header.switchLang")}
                </button>
                <a
                  href={`${home}#newsletter`}
                  onClick={closeMenu}
                  data-testid="mobile-header-cta"
                  className="inline-flex items-center justify-center px-4 py-3 text-sm font-bold rounded-md bg-[#006837] text-white hover:bg-[#004B28]"
                >
                  {t("header.cta")}
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
